import { useAuthStore } from '@/stores/auth'

/**
 * Hook to check if the current user has an active membership.
 * Only applies to members - other roles always return true.
 */
export function useHasActiveMembership(): boolean {
  const user = useAuthStore((state) => state.user)

  if (!user) return false

  // Non-members always have "access" (not membership-restricted)
  const isMember = user.roles.includes('member')
  if (!isMember) return true

  // For members, check actual membership status
  return user.membership?.status === 'active'
}

/**
 * Hook to check if the current user has an paused membership.
 * Only applies to members - other roles always return true.
 */
export function useHasPausedMembership(): boolean {
  const user = useAuthStore((state) => state.user)

  if (!user) return false

  // Non-members always have "access" (not membership-restricted)
  const isMember = user.roles.includes('member')
  if (!isMember) return true

  // For members, check actual membership status
  return user.membership?.status === 'paused'
}

/**
 * Hook to check if the current user has viewable membership (active or paused).
 * Only applies to members - other roles always return true.
 * Use this for routes where members should be able to view historical data.
 */
export function useHasViewableMembership(): boolean {
  const user = useAuthStore((state) => state.user)

  if (!user) return false

  // Non-members always have "access" (not membership-restricted)
  const isMember = user.roles.includes('member')
  if (!isMember) return true

  // For members, allow both active and paused statuses
  return user.membership?.status === 'active' || user.membership?.status === 'paused'
}

/**
 * Hook to check if the current user is a member (regardless of membership status)
 */
export function useIsMember(): boolean {
  const user = useAuthStore((state) => state.user)
  return user?.roles.includes('member') ?? false
}
